import { useState } from "react";
import type { CatalogConnector } from "../../shared/connector-catalog";
import type { ConnectorDTO, ConnectorPolicy, ConnectorProtectedRule } from "../../shared/types";
import { get, patch } from "../lib/api";
import { Tabs } from "./Tabs";
import { confirmDialog } from "./Dialog";
import { Badge, Button, ErrorText, Field, inputCls } from "./ui";
import { Switch } from "./Switch";

const GENERIC_TEMPLATES: { name: string; description: string; policy: ConnectorPolicy }[] = [
  { name: "Read-only", description: "Block all writes (POST/PUT/PATCH/DELETE).", policy: { readOnly: true } },
  { name: "No restrictions", description: "Everything the connector's key can do.", policy: {} },
];

const toLines = (v?: string[]) => (v ?? []).join("\n");
const fromLines = (s: string) => s.split(/\n/).map((l) => l.trim()).filter(Boolean);
const fromFieldLines = (s: string) => s.split(/\n|,/).map((l) => l.trim()).filter(Boolean);

export function policySummary(p: ConnectorPolicy | undefined): string | null {
  if (!p || (!p.readOnly && !p.allow?.length && !p.deny?.length && !p.redactFields?.length && !p.protected?.length)) return null;
  return [
    p.readOnly && "read-only",
    p.allow?.length && `${p.allow.length} allowed endpoints`,
    p.deny?.length && `${p.deny.length} blocked endpoints`,
    p.redactFields?.length && `${p.redactFields.length} hidden fields`,
    p.protected?.length && `${p.protected.length} protected ${p.protected.length === 1 ? "action" : "actions"}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/** What one protected rule asks for, in the words of a list: an action rule's label, or the template behind it. */
export function ruleWords(rule: ConnectorProtectedRule): string {
  if (rule.needs === "never") return `${rule.label}: blocked`;
  if (rule.needs === "person") return `${rule.label}: only with a person behind the run`;
  if (!rule.template) return `${rule.label}: a person's approval each time${rule.askOnSchedules ? ", on schedules too" : ""}`;
  return rule.needs === "released" ? `only on what a release of ${rule.template} made` : `an approved, unused ${rule.template}`;
}

/** `guarded` is carried through untouched: a protected action is the brake on a send, never edited by accident here. */
type Draft = { key?: string; name: string; readOnly: boolean; allow: string; deny: string; redact: string; autoAssignAdmins: boolean; guarded?: ConnectorProtectedRule[] };

export const toDraft = (name: string, p: ConnectorPolicy, key?: string, autoAssignAdmins = false): Draft => ({
  key, name, readOnly: Boolean(p.readOnly), allow: toLines(p.allow), deny: toLines(p.deny), redact: toLines(p.redactFields), autoAssignAdmins,
  guarded: p.protected,
});
export const toPolicy = (d: Draft): ConnectorPolicy => ({
  readOnly: d.readOnly, allow: fromLines(d.allow), deny: fromLines(d.deny), redactFields: fromFieldLines(d.redact),
  ...(d.guarded?.length ? { protected: d.guarded } : {}),
});

const NEW_LEVEL: Draft = { name: "New level", readOnly: true, allow: "", deny: "", redact: "", autoAssignAdmins: false };

/**
 * Default level (everyone) plus named levels that admins assign to specific users under Users → Data access.
 * `startAt` opens it on a level's key ("default", or "new" for a level being added); `embedded` drops its own heading,
 * for a place that already says what the levels are (a connector's Setup).
 */
export function PolicyEditor({ connector, entry, onSaved, startAt, embedded = false }: {
  connector: ConnectorDTO;
  entry?: CatalogConnector;
  onSaved: () => void | Promise<void>;
  startAt?: string;
  embedded?: boolean;
}) {
  const [drafts, setDrafts] = useState<Draft[]>(() => [
    toDraft("Everyone (default)", connector.policy ?? {}),
    ...(connector.levels ?? []).map((l) => toDraft(l.name, l.policy, l.key, Boolean(l.autoAssignAdmins))),
    ...(startAt === "new" ? [NEW_LEVEL] : []),
  ]);
  const [selected, setSelected] = useState(() =>
    startAt === "new" ? (connector.levels ?? []).length + 1 : Math.max(0, (connector.levels ?? []).findIndex((l) => l.key === startAt) + 1));
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const current = drafts[selected] ?? drafts[0]!;
  const update = (changes: Partial<Draft>) => {
    setDrafts((ds) => ds.map((d, i) => (i === selected ? { ...d, ...changes } : d)));
    setNotice(null);
  };
  const templates = [...(entry?.policyTemplates ?? []), ...GENERIC_TEMPLATES];

  const save = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // The default level's rules may have moved on the Tool permissions tab since this form opened: its copy is only what they were then.
      const current = await get<ConnectorDTO>(`/api/connectors/${connector.id}`);
      const saved = await patch<ConnectorDTO>(`/api/connectors/${connector.id}`, {
        policy: toPolicy({ ...drafts[0]!, guarded: current.policy.protected }),
        levels: drafts.slice(1).map((d) => ({ key: d.key, name: d.name, policy: toPolicy(d), autoAssignAdmins: d.autoAssignAdmins })),
      });
      // A level added here gets its key from the save, so saving again edits it rather than adding it twice.
      setDrafts((ds) => ds.map((d, i) => (i === 0 ? d : { ...d, key: saved.levels[i - 1]?.key ?? d.key })));
      await onSaved();
      setNotice("Saved. Applies to the next call.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`space-y-3 ${embedded ? "" : "border-t border-line pt-4"}`}>
      {!embedded && (
        <div>
          <h3 className="text-sm font-semibold">Access levels</h3>
          <p className="text-xs text-muted">
            Access follows the person asking — in the web app, Slack DMs and channels. Everyone gets the default level; give specific people another level under Users → Data access,
            or let admins get a level automatically. A person's schedule runs as its owner, with their level; one that runs as the agent itself gets the agent's level and workspace key. Enforced by the platform — agents cannot bypass it.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Tabs
          label="Access level"
          tabs={drafts.map((d, i) => ({ key: String(i), label: d.name || "Unnamed" }))}
          value={String(selected)}
          onChange={(key) => setSelected(Number(key))}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDrafts((ds) => [...ds, NEW_LEVEL]);
            setSelected(drafts.length);
          }}
        >
          + Add level
        </Button>
      </div>

      <div className="space-y-3 rounded-md border border-line p-3">
        {selected > 0 ? (
          <>
            <div className="flex items-end gap-2">
              <Field label="Level name">
                <input className={inputCls} value={current.name} onChange={(e) => update({ name: e.target.value })} />
              </Field>
              <Button
                variant="danger"
                onClick={async () => {
                  if (!(await confirmDialog({ title: `Remove the "${current.name}" level?`, body: "People assigned to it go back to the default level when you save.", confirm: "Remove level", danger: true }))) return;
                  setDrafts((ds) => ds.filter((_, i) => i !== selected));
                  setSelected(0);
                }}
              >
                Remove
              </Button>
            </div>
            <Switch label="Admins get this level automatically" checked={current.autoAssignAdmins} onChange={(autoAssignAdmins) => update({ autoAssignAdmins })} />
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm">
            <Badge>default</Badge>
            <span className="text-xs text-muted">Applies to every user without an assigned level.</span>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs text-muted">Templates:</span>
          {templates.map((t) => (
            <Button
              key={t.name}
              type="button"
              size="sm"
              title={t.description}
              onClick={() => update({ readOnly: Boolean(t.policy.readOnly), allow: toLines(t.policy.allow), deny: toLines(t.policy.deny), redact: toLines(t.policy.redactFields) })}
            >
              {t.name}
            </Button>
          ))}
        </div>

        <Switch label="Read-only" hint="Blocks POST, PUT, PATCH and DELETE." checked={current.readOnly} onChange={(readOnly) => update({ readOnly })} />
        <Field label="Allowed endpoints" hint='One per line, e.g. "GET /time_off/*". Empty = all endpoints. * matches one path segment, ** any.'>
          <textarea className={`${inputCls} font-mono text-xs`} rows={4} value={current.allow} onChange={(e) => update({ allow: e.target.value })} placeholder="GET /employees/directory&#10;GET /time_off/*" />
        </Field>
        <Field label="Blocked endpoints" hint='Always refused at this level, even if allowed above. Methods can be combined: "PUT,DELETE /repos/*/*/collaborators/*".'>
          <textarea className={`${inputCls} font-mono text-xs`} rows={4} value={current.deny} onChange={(e) => update({ deny: e.target.value })} placeholder="PUT,DELETE /repos/*/*/collaborators/*" />
        </Field>
        <Field label="Hidden fields" hint="JSON keys (or report field names) removed from responses before agents and logs see them.">
          <textarea className={`${inputCls} font-mono text-xs`} rows={3} value={current.redact} onChange={(e) => update({ redact: e.target.value })} placeholder="payRate&#10;ssn&#10;dateOfBirth" />
        </Field>
        {current.guarded?.length ? (
          <Field label="Protected actions" hint="Set on the Tool permissions tab, and kept as they are when this form is saved. Each one needs a person's approval behind it before the proxy forwards it, or is never forwarded.">
            <ul className="space-y-1 font-mono text-xs text-muted">
              {current.guarded.map((rule) => (
                <li key={`${rule.method} ${rule.path}`}>
                  {rule.method} {rule.path} — {ruleWords(rule)}
                </li>
              ))}
            </ul>
          </Field>
        ) : null}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="primary" disabled={busy} onClick={save}>
          {busy ? "Saving…" : "Save access levels"}
        </Button>
        {notice && <span className="text-xs text-success">{notice}</span>}
      </div>
      <ErrorText error={error} />
    </div>
  );
}
